import React, { useEffect, useState } from "react";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import BlockIcon from "@mui/icons-material/Block";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PublishIcon from "@mui/icons-material/Publish";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import StoryImageZoomViewport from "./StoryImageZoomViewport";

function statusText(state) {
    const value = String(state || "").trim();
    if (!value) return "";
    return value
        .toLowerCase()
        .split("_")
        .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : ""))
        .join(" ");
}

function statusColor(state) {
    const value = String(state || "").toUpperCase();
    if (value === "PUBLISHED") return "success";
    if (value === "REVIEWED") return "primary";
    if (value === "DISABLED") return "warning";
    return "default";
}

function canPublishState(state, standalone, allowDisabled) {
    const value = String(state || "").toUpperCase();
    if (value === "REVIEWED") return true;
    if (allowDisabled && value === "DISABLED") return true;
    if (standalone && value === "DRAFT") return true;
    return false;
}

export default function StoryImageZoomDialog({
    open,
    imageUrl,
    title,
    onClose,
    onDelete,
    deleting,
    onDisable,
    disabling,
    onRegen,
    regenLoading,
    onPrev,
    onNext,
    hasPrev = false,
    hasNext = false,
    indexLabel,
    loading = false,
    extra,
    free,
    onFreeChange,
    savingFree = false,
    state,
    standalone = false,
    onPublish,
    publishing = false,
    allowPublishDisabled = false,
}) {
    const canPrev = typeof onPrev === "function";
    const canNext = typeof onNext === "function";
    const showPublish = typeof onPublish === "function" && canPublishState(state, standalone, allowPublishDisabled);
    const label = statusText(state);
    const busy = Boolean(deleting) || loading || Boolean(regenLoading) || Boolean(disabling) || Boolean(publishing);
    const [toolbarEl, setToolbarEl] = useState(null);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => {
            if (event.key === "ArrowLeft") {
                event.preventDefault();
                if (hasPrev && !busy) onPrev?.();
            } else if (event.key === "ArrowRight") {
                event.preventDefault();
                if (hasNext && !busy) onNext?.();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, hasPrev, hasNext, onPrev, onNext, busy]);

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        display: "flex",
                        flexDirection: "column",
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto auto",
                    alignItems: "center",
                    columnGap: 1,
                    py: 1,
                    pr: 1,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
                        {title || "Story image"}
                    </Typography>
                    {label ? (
                        <Chip
                            size="small"
                            label={label}
                            color={statusColor(state)}
                            sx={{ flexShrink: 0, fontWeight: 600 }}
                        />
                    ) : null}
                </Stack>
                <Box ref={setToolbarEl} sx={{ display: "flex", justifyContent: "center" }} />
                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                    {indexLabel ? (
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                            {indexLabel}
                        </Typography>
                    ) : null}
                    {typeof onFreeChange === "function" ? (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={free === true}
                                    disabled={Boolean(savingFree) || busy}
                                    onChange={(event) => onFreeChange(event.target.checked)}
                                    inputProps={{ "aria-label": "Free" }}
                                />
                            }
                            label="Free"
                            sx={{ mr: 0.5, flexShrink: 0, minHeight: 44 }}
                        />
                    ) : null}
                    {showPublish ? (
                        <Button
                            onClick={onPublish}
                            disabled={busy}
                            variant="contained"
                            startIcon={
                                publishing ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />
                            }
                            sx={{ textTransform: "none", minHeight: 44, flexShrink: 0 }}
                        >
                            {publishing ? "Publishing…" : "Publish"}
                        </Button>
                    ) : null}
                    {typeof onDelete === "function" ? (
                        <Button
                            onClick={onDelete}
                            disabled={busy}
                            color="error"
                            startIcon={
                                deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
                            }
                            sx={{ textTransform: "none", minHeight: 44, flexShrink: 0 }}
                        >
                            {deleting ? "Deleting…" : "Delete"}
                        </Button>
                    ) : null}
                    {typeof onDisable === "function" ? (
                        <Button
                            onClick={onDisable}
                            disabled={busy}
                            startIcon={
                                disabling ? <CircularProgress size={16} color="inherit" /> : <BlockIcon />
                            }
                            sx={{ textTransform: "none", minHeight: 44, flexShrink: 0, color: "grey.700" }}
                        >
                            {disabling ? "Disabling…" : "Disable"}
                        </Button>
                    ) : null}
                    {typeof onRegen === "function" ? (
                        <Button
                            onClick={onRegen}
                            disabled={busy}
                            startIcon={
                                regenLoading ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />
                            }
                            sx={{ textTransform: "none", minHeight: 44, flexShrink: 0 }}
                        >
                            {regenLoading ? "Regenerating…" : "Re-generate image"}
                        </Button>
                    ) : null}
                    <IconButton
                        aria-label="Close"
                        onClick={onClose}
                        disabled={busy}
                        sx={{ width: 44, height: 44 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    p: { xs: 1, sm: 1.5 },
                    display: "flex",
                    flexDirection: "column",
                    gap: extra ? 1 : 0,
                }}
            >
                <Box sx={{ flex: 1, minHeight: 0, position: "relative", display: "flex" }}>
                <StoryImageZoomViewport
                    imageUrl={imageUrl}
                    fillHeight
                    hideToolbar
                    toolbarHost={toolbarEl}
                    toolbarButtonSx={{ width: 44, height: 44 }}
                    sx={{ flex: 1, minHeight: 0 }}
                />
                {canPrev ? (
                    <IconButton
                        aria-label="Previous image"
                        disabled={!hasPrev || busy}
                        onClick={onPrev}
                        sx={{
                            position: "absolute",
                            left: { xs: 8, sm: 16 },
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 44,
                            height: 44,
                            bgcolor: "rgba(0,0,0,0.45)",
                            color: "common.white",
                            zIndex: 2,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
                            "&.Mui-disabled": { color: "rgba(255,255,255,0.35)", bgcolor: "rgba(0,0,0,0.25)" },
                        }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                ) : null}
                {canNext ? (
                    <IconButton
                        aria-label="Next image"
                        disabled={!hasNext || busy}
                        onClick={onNext}
                        sx={{
                            position: "absolute",
                            right: { xs: 8, sm: 16 },
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 44,
                            height: 44,
                            bgcolor: "rgba(0,0,0,0.45)",
                            color: "common.white",
                            zIndex: 2,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
                            "&.Mui-disabled": { color: "rgba(255,255,255,0.35)", bgcolor: "rgba(0,0,0,0.25)" },
                        }}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                ) : null}
                {loading || regenLoading || disabling || publishing ? (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "rgba(0,0,0,0.25)",
                            zIndex: 3,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : null}
                </Box>
                {extra ? (
                    <Box sx={{ flexShrink: 0, minWidth: 0 }}>
                        {extra}
                    </Box>
                ) : null}
            </Box>
        </Dialog>
    );
}
